import React, { forwardRef, useRef, useState, useImperativeHandle, useMemo } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import { useForm } from "react-hook-form";
import LovUsers from "src/components/Lov/Users";
import Title from "src/components/Title";
import { v4 } from "uuid";
import { IAction } from "src/pages/Actions/Panel/interface";
import Modal from "src/components/Modal";
import returnStringValueForSelect from "src/utils/returnStringValueForSelect";
import { IReleaseActions, IReleaseActionsRef } from "./interface";
import SchemaReleaseActions from "./schema";

const ModalReleaseActionsInDraft: React.ForwardRefRenderFunction<IReleaseActionsRef, IReleaseActions> = ({
	relatedTableId,
	relatedMethodController,
	subtitle,
	onCallbackRelease
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
    
	// Modal Release Actions
	const [actionsInDraft, setActionsInDraft] = useState<IAction[]>();

	const modalReleaseActions = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		control: controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange,
		watch: watchAddOrChange,
		unregister: unregisterAddOrChange
	} = useForm();

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalReleaseActions.current?.setLoadingButton();
			setActionsInDraft(data.intbb001);

			await SchemaReleaseActions(translate).validate({...data}, {
				abortEarly: false
			});
			
			await RCCServicesApi.putRequest({
				method: relatedMethodController,
				body: {
					...data,
					id: relatedTableId
				}
			});
			
			if(onCallbackRelease)
				onCallbackRelease();

			addToast({
				color: "green",
				description: translate("Ações liberadas com sucesso"),
				title: translate("Sucesso")
			});

			modalReleaseActions.current?.resetLoadingButton();
			modalReleaseActions.current?.closeModal();

		}catch(err){
			modalReleaseActions.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
			standardErrorMessages(err, translate("Erro ao liberar ações"), addToast);
		}
	};

	const applyInAllActions = () => {
		const inb001Efa005Id = watchAddOrChange("responsible");
		const inb001Efa005Id2 = watchAddOrChange("authority");
		const inb001Dtinicio = watchAddOrChange("dateStart");
		const inb001Dtfim = watchAddOrChange("dateEnd");

		const inb001Efa005IdChanged = returnStringValueForSelect(inb001Efa005Id);
		const inb001Efa005Id2Changed = returnStringValueForSelect(inb001Efa005Id2);

		const newActions = actionsInDraft?.map((action: IAction) => ({
			...action,
			inb001Efa005Id: inb001Efa005IdChanged ? parseInt(inb001Efa005IdChanged,10) : action.inb001Efa005Id,
			inb001Efa005Id2: inb001Efa005Id2Changed ? parseInt(inb001Efa005Id2Changed,10) : action.inb001Efa005Id2,
			inb001Dtinicio: inb001Dtinicio ?? action.inb001Dtinicio,
			inb001Dtfim: inb001Dtfim ?? action.inb001Dtfim
		}));

		unregisterAddOrChange();
		setActionsInDraft(newActions);
	};

	const openModalReleaseActionsInDraft = (actions: IAction[]) => {
		resetAddOrChange();
		setActionsInDraft(actions);
		modalReleaseActions.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		openModalReleaseActionsInDraft
	}));

	const ActionsInDraft = useMemo(() => (
		<>
			{actionsInDraft && actionsInDraft.map((action: IAction, index: any) => (
				<RCCGrid
					container
					item
					key={`action-${v4()}`}
					spacing={1}
					styleInline="border-bottom: 1px solid var(--border-color); width: 100% !important; margin: 0 !important;"
				>
					<input 
						type="text"
						value={action.inb001Id}
						{...registerAddOrChange(`intbb001[${index}].inb001Id`)} 
						style={{ display: "none" }} 
						readOnly
					/>

					<input 
						type="text"
						value={action.inb001Codigo}
						{...registerAddOrChange(`intbb001[${index}].inb001Codigo`)} 
						style={{ display: "none" }} 
						readOnly
					/>

					<input 
						type="text"
						value={action.inb001Titulo}
						{...registerAddOrChange(`intbb001[${index}].inb001Titulo`)} 
						style={{ display: "none" }} 
						readOnly
					/>

					<RCCGrid
						container
						item
						md={12}
						xs={12}
						alignItems="center"
						styleInline="
								font-size: 0.8rem;
								font-weight: bold;"
					>
						<span title={`${action.inb001Codigo} - ${action.inb001Titulo}`}>{`${action.inb001Codigo} - ${action.inb001Titulo}`}</span>
					</RCCGrid>
					<RCCGrid
						container
						item
						md={4}
						xs={12}
					>
						<LovUsers 
							control={controlAddOrChange}
							name={`intbb001[${index}].inb001Efa005Id`}
							defaultValue={returnStringValueForSelect(action.inb001Efa005Id)}
							label={translate("Responsável")}
							isClearable
							error={errorsAddOrChange.intbb001 && errorsAddOrChange.intbb001[index]?.inb001Efa005Id}
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						md={4}
						xs={12}
					>
						<LovUsers 
							control={controlAddOrChange}
							name={`intbb001[${index}].inb001Efa005Id2`}
							defaultValue={returnStringValueForSelect(action.inb001Efa005Id2)}
							label={translate("Autoridade")}
							isClearable
							error={errorsAddOrChange.intbb001 && errorsAddOrChange.intbb001[index]?.inb001Efa005Id2}
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						md={4}
						xs={12}
					>
						<DateTimePickerRange
							control={controlAddOrChange}
							type="datePicker"
							label={translate("Período")}
							labelSeparator={translate("Até")}
							nameInit={`intbb001[${index}].inb001Dtinicio`}
							nameEnd={`intbb001[${index}].inb001Dtfim`}
							defaultValueInit={action?.inb001Dtinicio ? action.inb001Dtinicio : null}
							defaultValueEnd={action?.inb001Dtfim ? action.inb001Dtfim : null}
							error={errorsAddOrChange.intbb001 && (errorsAddOrChange.intbb001[index]?.inb001Dtinicio || errorsAddOrChange.intbb001[index]?.pra003Dtfimest)}
						/>
					</RCCGrid>
				</RCCGrid>
			))}
		</>
	), [actionsInDraft]);

	return (
		<>
			<Modal
				ref={modalReleaseActions}
				header={{
					title: translate("Liberar Ações em Rascunho"),
					subtitle,
					icon: {
						name: "listCheck"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				footer= {{
					showSaveButton: true,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Liberar Ações"),
					cancelButtonText: translate("Cancelar")
				}}
			>
				<RCCGrid
					container
					item
					spacing={1}
					styleInline="margin-bottom: 2rem !important;"
				>	
					<RCCGrid
						container
						item
						xs={12}
					>
						<Title 
							color="purple"
						>
							{translate("Aplicar em todas as ações")}
						</Title>
					</RCCGrid>				
					<RCCGrid
						container
						item
						md={3}
						xs={12}
					>
						<LovUsers 
							control={controlAddOrChange}
							name="responsible"
							label={translate("Responsável")}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						md={3}
						xs={12}
					>
						<LovUsers 
							control={controlAddOrChange}
							name="authority"
							label={translate("Autoridade")}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						md={5}
						xs={12}
					>
						<DateTimePickerRange
							control={controlAddOrChange}
							type="datePicker"
							label={translate("Período")}
							labelSeparator={translate("Até")}
							nameInit="dateStart"
							nameEnd="dateEnd"
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						md={1}
						xs={12}
						alignItems="flex-end"
						justifyContent="flex-end"
					>
						<RCCTooltip
							color="blue"
							position="bottom"
							text={translate("Aplicar")}
						>
							<RCCButton
								color="blue"
								icon={{
									name: "checkCircle"
								}}
								layout="isRounded"
								size="md"
								onClick={applyInAllActions}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
				<Title 
					color="purple"
					marginBottom={15}
				>
					{translate("Ações")}
				</Title>
				<hr />
				{ActionsInDraft}
			</Modal>
		</>
	);
};

export default forwardRef(ModalReleaseActionsInDraft);
